import type { JSX } from "preact";
import type { PankAgentVariant } from "./Icons";

export interface MarkdownRendererProps {
  content: string;
  variant?: PankAgentVariant;
}

interface ParsedToken {
  type: "text" | "bold" | "newline";
  content: string;
}

interface OrderedListItem {
  number: number;
  content: string;
}

interface ContentBlock {
  type: "paragraph" | "ordered-list";
  content?: string;
  items?: OrderedListItem[];
}

/**
 * Parse inline markdown (bold text)
 */
const parseInlineMarkdown = (text: string): ParsedToken[] => {
  const tokens: ParsedToken[] = [];
  const boldPattern = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = boldPattern.exec(text)) !== null) {
    // Add text before the bold
    if (match.index > lastIndex) {
      tokens.push({
        type: "text",
        content: text.slice(lastIndex, match.index),
      });
    }
    // Add bold text
    tokens.push({
      type: "bold",
      content: match[1] ?? "",
    });
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    tokens.push({
      type: "text",
      content: text.slice(lastIndex),
    });
  }

  return tokens;
};

/**
 * Parse content into blocks (paragraphs and ordered lists)
 */
const parseContentBlocks = (content: string): ContentBlock[] => {
  const blocks: ContentBlock[] = [];
  const lines = content.split("\n");
  let currentListItems: OrderedListItem[] = [];
  let currentParagraph: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      blocks.push({
        type: "paragraph",
        content: currentParagraph.join("\n"),
      });
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (currentListItems.length > 0) {
      blocks.push({
        type: "ordered-list",
        items: [...currentListItems],
      });
      currentListItems = [];
    }
  };

  for (const line of lines) {
    // Check for ordered list item (e.g., "1. Step one")
    const listMatch = line.match(/^(\d+)\.\s+(.+)$/);

    if (listMatch) {
      flushParagraph();
      currentListItems.push({
        number: parseInt(listMatch[1] ?? "0", 10),
        content: listMatch[2] ?? "",
      });
    } else {
      flushList();
      if (line.trim() === "") {
        flushParagraph();
      } else {
        currentParagraph.push(line);
      }
    }
  }

  flushParagraph();
  flushList();

  return blocks;
};

/**
 * Render inline tokens with proper styling
 */
const InlineContent = ({
  tokens,
  variant,
}: {
  tokens: ParsedToken[];
  variant: PankAgentVariant;
}): JSX.Element => {
  return (
    <>
      {tokens.map((token, index) => {
        if (token.type === "bold") {
          return (
            <strong key={index} class={`pankagent-md-bold variant-${variant}`}>
              {token.content}
            </strong>
          );
        }
        return <span key={index}>{token.content}</span>;
      })}
    </>
  );
};

/**
 * Render an ordered list with circular numbered indicators
 */
const OrderedList = ({
  items,
  variant,
}: {
  items: OrderedListItem[];
  variant: PankAgentVariant;
}): JSX.Element => {
  return (
    <div class="pankagent-md-ordered-list">
      {items.map((item, index) => (
        <div key={index} class="pankagent-md-list-item">
          <div class={`pankagent-md-list-number variant-${variant}`}>
            <span>{item.number}</span>
          </div>
          <div class="pankagent-md-list-content">
            <InlineContent
              tokens={parseInlineMarkdown(item.content)}
              variant={variant}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Lightweight Markdown renderer for chat messages
 * Supports:
 * - Bold text (**text**) - colored based on agent variant
 * - Ordered lists (1. item) - with circular numbered indicators
 * - Line breaks
 */
export const MarkdownRenderer = ({
  content,
  variant = "default",
}: MarkdownRendererProps): JSX.Element => {
  const blocks = parseContentBlocks(content);

  return (
    <div class="pankagent-md-content">
      {blocks.map((block, index) => {
        if (block.type === "ordered-list" && block.items) {
          return (
            <OrderedList key={index} items={block.items} variant={variant} />
          );
        }

        if (block.type === "paragraph" && block.content) {
          const tokens = parseInlineMarkdown(block.content);
          return (
            <p key={index} class="pankagent-md-paragraph">
              <InlineContent tokens={tokens} variant={variant} />
            </p>
          );
        }

        return null;
      })}
    </div>
  );
};
